import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';
import Navbar from '@/pages/home/components/Navbar';
import { articleSEOData, type ArticleSEO } from '@/mocks/articles';

const SITE_URL = typeof import.meta !== 'undefined' && (import.meta as unknown as Record<string, string>).env?.VITE_SITE_URL
  ? (import.meta as unknown as Record<string, Record<string, string>>).env.VITE_SITE_URL
  : 'https://kakobuyspreadsheet.work';

function getSlugFromPath(pathname: string): string {
  return pathname.replace(/^\/+/, '').replace(/\/+$/, '');
}

function getArticleSEO(slug: string): ArticleSEO | undefined {
  return articleSEOData[slug];
}

export default function ArticlePage() {
  const location = useLocation();
  const slug = useMemo(() => getSlugFromPath(location.pathname), [location.pathname]);
  const seo = useMemo(() => getArticleSEO(slug), [slug]);

  const pageTitle = seo?.title || 'Kakobuy Spreadsheet Polska';
  const pageDescription = seo?.description || 'Kompletny przewodnik po arkuszach produktowych Kakobuy.';
  const pageKeywords = seo?.keywords || 'kakobuy spreadsheet, kakobuy polska';
  const articleUrl = `${SITE_URL}/${slug}`;
  const categoryLabel = seo?.categoryLabel || 'Poradniki';
  const readingTime = seo?.readingTime || '10 min czytania';
  const datePublished = seo?.datePublished || '2026-06-21';
  const articleHeadline = seo?.title || 'Artykuł Kakobuy Spreadsheet';

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = pageTitle;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageDescription);
    }
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', pageKeywords);
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', pageTitle);
    }
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute('content', pageDescription);
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', articleUrl);
    }
    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', 'article');
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', pageTitle);
    }
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) {
      twitterDesc.setAttribute('content', pageDescription);
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', articleUrl);
    }

    return () => {
      document.title = 'Kakobuy Spreadsheet Polska - Kompletny Przewodnik po Arkuszach Produktowych';
    };
  }, [pageTitle, pageDescription, pageKeywords, articleUrl]);

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': articleHeadline,
    'author': { '@type': 'Organization', 'name': 'Kakobuy Spreadsheet Polska' },
    'publisher': { '@type': 'Organization', 'name': 'Kakobuy Spreadsheet Polska' },
    'datePublished': datePublished,
    'dateModified': '2026-06-21',
    'description': pageDescription,
    'inLanguage': 'pl-PL',
    'url': articleUrl,
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Strona główna', 'item': SITE_URL },
      { '@type': 'ListItem', 'position': 2, 'name': categoryLabel, 'item': articleUrl },
      { '@type': 'ListItem', 'position': 3, 'name': articleHeadline, 'item': articleUrl },
    ],
  };

  const jsonLdFAQ = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      { '@type': 'Question', 'name': 'Czy Kakobuy Spreadsheet jest darmowy?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Tak, dostęp do arkusza Kakobuy Spreadsheet jest całkowicie darmowy. Naszym celem jest ułatwienie dostępu do sprawdzonych produktów dla wszystkich użytkowników platformy Kakobuy.' } },
      { '@type': 'Question', 'name': 'Jak często arkusz jest aktualizowany?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Arkusz jest aktualizowany regularnie, zazwyczaj co kilka dni. Staramy się dodawać nowe produkty i usuwać nieaktualne linki.' } },
      { '@type': 'Question', 'name': 'Czy potrzebuję konta na Kakobuy aby korzystać z arkusza?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Do przeglądania arkusza nie potrzebujesz konta. Jednak do dokonania zakupu na platformie Kakobuy konieczne jest posiadanie aktywnego konta użytkownika.' } },
      { '@type': 'Question', 'name': 'Skąd pochodzą linki w arkuszu?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Linki pochodzą od doświadczonych użytkowników platformy Kakobuy, którzy testują produkty i weryfikują sprzedawców.' } },
      { '@type': 'Question', 'name': 'Co zrobić jeśli link nie działa?', 'acceptedAnswer': { '@type': 'Answer', 'text': 'Jeśli znajdziesz nieaktywny link, skontaktuj się z nami. Staramy się regularnie weryfikować wszystkie linki.' } },
    ],
  };

  return (
    <>
      <Navbar />
      <article className="min-h-screen bg-background-50">
        {/* Article Header */}
        <header className="pt-24 pb-10 md:pt-32 md:pb-14 bg-background-100/60">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="mb-6" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm font-body">
                <li>
                  <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
                    Strona główna
                  </Link>
                </li>
                <li className="text-foreground-300">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-500">{categoryLabel}</li>
                <li className="text-foreground-300">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-700 font-medium truncate">
                  {articleHeadline}
                </li>
              </ol>
            </nav>

            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-label mb-4">
              {categoryLabel}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              {articleHeadline}
            </h1>
            <p className="text-foreground-500 text-base md:text-lg leading-relaxed mb-6 font-body max-w-3xl">
              {pageDescription}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 font-body">
              <span className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                Zespół Kakobuy Spreadsheet Polska
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                {datePublished}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                {readingTime}
              </span>
            </div>
          </div>
        </header>

        {/* Article Body */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="prose-custom">

            {/* Introduction */}
            <p className="text-foreground-700 text-base md:text-lg leading-relaxed mb-8 font-body">
              W świecie zakupów przez agenta, <strong className="text-foreground-950">Kakobuy Spreadsheet</strong> stał się jednym z najważniejszych narzędzi dla użytkowników poszukujących sprawdzonych produktów. Jeśli dopiero zaczynasz swoją przygodę z platformą Kakobuy lub szukasz sposobu na efektywniejsze przeglądanie ofert, ten przewodnik jest właśnie dla Ciebie. W tym artykule wyjaśnimy dokładnie czym jest <strong className="text-foreground-950">arkusz Kakobuy</strong>, jak z niego korzystać i dlaczego tysiące polskich użytkowników wybiera właśnie to rozwiązanie.
            </p>

            {/* Table of Contents */}
            <div className="bg-background-100/80 rounded-2xl p-6 md:p-8 mb-10 border border-background-200/50">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">Spis treści</h2>
              <ol className="space-y-2 text-sm font-body">
                <li><a href="#czym-jest" className="text-primary-500 hover:text-primary-600 transition-colors">1. Czym jest Kakobuy Spreadsheet?</a></li>
                <li><a href="#jak-dziala" className="text-primary-500 hover:text-primary-600 transition-colors">2. Jak działa arkusz produktowy Kakobuy?</a></li>
                <li><a href="#dlaczego-warto" className="text-primary-500 hover:text-primary-600 transition-colors">3. Dlaczego warto korzystać z arkusza?</a></li>
                <li><a href="#kategorie" className="text-primary-500 hover:text-primary-600 transition-colors">4. Dostępne kategorie produktów</a></li>
                <li><a href="#jak-korzystac" className="text-primary-500 hover:text-primary-600 transition-colors">5. Jak korzystać z arkusza krok po kroku</a></li>
                <li><a href="#porownanie" className="text-primary-500 hover:text-primary-600 transition-colors">6. Porównanie z innymi narzędziami</a></li>
                <li><a href="#wskazowki" className="text-primary-500 hover:text-primary-600 transition-colors">7. Praktyczne wskazówki</a></li>
                <li><a href="#faq" className="text-primary-500 hover:text-primary-600 transition-colors">8. FAQ &mdash; Najczęściej zadawane pytania</a></li>
              </ol>
            </div>

            {/* Section 1 */}
            <section id="czym-jest" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                1. Czym jest Kakobuy Spreadsheet?
              </h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-4 font-body">
                <strong className="text-foreground-950">Kakobuy Spreadsheet</strong> to starannie przygotowany arkusz kalkulacyjny, który zawiera bezpośrednie linki do produktów dostępnych na platformie Kakobuy. W przeciwieństwie do standardowego przeglądania platformy, arkusz oferuje uporządkowaną i skategoryzowaną strukturę, która umożliwia szybkie odnajdywanie interesujących produktów.
              </p>
              <p className="text-foreground-700 text-base leading-relaxed mb-4 font-body">
                Arkusz jest regularnie aktualizowany przez doświadczonych użytkowników, którzy testują produkty i weryfikują sprzedawców. Dzięki temu masz pewność, że linki prowadzą do sprawdzonych ofert, a nie do przypadkowych lub niskiej jakości produktów.
              </p>

              <div className="bg-primary-50/50 rounded-xl p-5 md:p-6 border border-primary-200/40 my-6">
                <p className="text-foreground-800 text-sm md:text-base leading-relaxed font-body">
                  <strong className="text-foreground-950">Kluczowa informacja:</strong> Kakobuy Spreadsheet nie jest oficjalnym produktem platformy Kakobuy. To niezależny przewodnik tworzony przez społeczność użytkowników, których celem jest ułatwienie dostępu do najlepszych produktów na platformie.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section id="jak-dziala" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                2. Jak działa arkusz produktowy Kakobuy?
              </h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-4 font-body">
                System arkuszy Kakobuy opiera się na prostej, ale niezwykle efektywnej zasadzie organizacji danych. Każdy produkt w arkuszu posiada następujące informacje:
              </p>
              <ul className="space-y-3 mb-6 font-body">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-accent-600"></i>
                  </div>
                  <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Nazwa produktu</strong> &mdash; dokładny opis wraz z wariantami kolorystycznymi i rozmiarowymi</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-accent-600"></i>
                  </div>
                  <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Bezpośredni link</strong> &mdash; klikalny odnośnik prowadzący prosto do produktu na platformie Kakobuy</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-accent-600"></i>
                  </div>
                  <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Kategoria</strong> &mdash; przypisanie do odpowiedniej grupy produktowej (Shoes, Hoodies, T-Shirts itd.)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-accent-600"></i>
                  </div>
                  <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Ocena sprzedawcy</strong> &mdash; informacja o wiarygodności i jakości obsługi danego sprzedawcy</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs text-accent-600"></i>
                  </div>
                  <span className="text-foreground-700 text-base"><strong className="text-foreground-950">Notatki i uwagi</strong> &mdash; dodatkowe informacje od testerów, np. o jakości materiału czy zgodności rozmiarów</span>
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="dlaczego-warto" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                3. Dlaczego warto korzystać z arkusza Kakobuy?
              </h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-4 font-body">
                Korzystanie z <strong className="text-foreground-950">Kakobuy Spreadsheet</strong> niesie ze sobą szereg korzyści, które znacząco poprawiają doświadczenia zakupowe. Oto najważniejsze z nich:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                    <i className="ri-time-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Oszczędność czasu</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed font-body">Zamiast godzinami przeszukiwać platformę, znajdujesz produkty w kilka sekund dzięki uporządkowanym kategoriom i bezpośrednim linkom.</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                    <i className="ri-shield-check-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Sprawdzeni sprzedawcy</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed font-body">Linki prowadzą wyłącznie do zweryfikowanych sprzedawców z dobrą reputacją, co minimalizuje ryzyko oszustwa.</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                    <i className="ri-stack-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Pełna organizacja</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed font-body">Wszystkie produkty są pogrupowane według kategorii i marek, co ułatwia nawigację i planowanie zakupów.</p>
                </div>
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 mb-3">
                    <i className="ri-refresh-line text-lg text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Regularne aktualizacje</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed font-body">Arkusz jest stale aktualizowany &mdash; dodawane są nowe produkty, a nieaktualne linki są usuwane.</p>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section id="kategorie" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                4. Dostępne kategorie produktów
              </h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-6 font-body">
                <strong className="text-foreground-950">Kakobuy Spreadsheet</strong> obejmuje szeroki zakres kategorii produktowych. Poniżej znajduje się pełna lista dostępnych kategorii wraz z krótkim opisem:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm font-body border-collapse">
                  <thead>
                    <tr className="bg-background-100/80">
                      <th className="text-left px-4 py-3 text-foreground-950 font-label font-semibold text-xs uppercase tracking-wider rounded-tl-lg">Kategoria</th>
                      <th className="text-left px-4 py-3 text-foreground-950 font-label font-semibold text-xs uppercase tracking-wider">Opis</th>
                      <th className="text-left px-4 py-3 text-foreground-950 font-label font-semibold text-xs uppercase tracking-wider rounded-tr-lg">Popularność</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Shoes', 'Buty sportowe, casualowe i eleganckie', '⭐⭐⭐⭐⭐'],
                      ['Hoodies/Sweaters', 'Bluzy, swetry i odzież wierzchnia', '⭐⭐⭐⭐⭐'],
                      ['T-Shirts', 'Koszulki, t-shirty i topy', '⭐⭐⭐⭐⭐'],
                      ['Jackets', 'Kurtki, płaszcze i wiatrówki', '⭐⭐⭐⭐'],
                      ['Pants/Shorts', 'Spodnie, szorty i dresy', '⭐⭐⭐⭐'],
                      ['Headwear', 'Czapki, kapelusze i akcesoria na głowę', '⭐⭐⭐'],
                      ['Sets', 'Zestawy i komplety ubrań', '⭐⭐⭐⭐'],
                      ['Jersey', 'Koszulki sportowe i piłkarskie', '⭐⭐⭐⭐'],
                      ['Accessories', 'Akcesoria i dodatki modowe', '⭐⭐⭐⭐'],
                      ['Bags', 'Torby, plecaki i walizki', '⭐⭐⭐'],
                    ].map((row, idx) => (
                      <tr key={idx} className="border-b border-background-200/50 hover:bg-background-50/80 transition-colors">
                        <td className="px-4 py-3 text-foreground-800 font-medium whitespace-nowrap">{row[0]}</td>
                        <td className="px-4 py-3 text-foreground-600">{row[1]}</td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 5 */}
            <section id="jak-korzystac" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                5. Jak korzystać z arkusza Kakobuy krok po kroku
              </h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-6 font-body">
                Oto szczegółowa instrukcja jak efektywnie korzystać z <strong className="text-foreground-950">arkusza Kakobuy</strong>:
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0">
                    <span className="text-background-50 font-label font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-1">Wybierz kategorię</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed font-body">Zacznij od wybrania interesującej Cię kategorii produktowej. Możesz to zrobić poprzez menu kategorii na górze strony lub przeglądając pełną listę dostępnych kategorii.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0">
                    <span className="text-background-50 font-label font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-1">Przeglądaj produkty</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed font-body">Po wybraniu kategorii zobaczysz listę dostępnych produktów wraz ze zdjęciami, opisami i linkami. Każdy produkt posiada ocenę sprzedawcy.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0">
                    <span className="text-background-50 font-label font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-1">Kliknij w link do produktu</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed font-body">Po znalezieniu interesującego produktu kliknij w bezpośredni link, który przeniesie Cię na stronę produktu na platformie Kakobuy.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0">
                    <span className="text-background-50 font-label font-bold text-sm">4</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-1">Sprawdź szczegóły i kup</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed font-body">Na stronie produktu sprawdź dostępne warianty, rozmiary i kolory. Następnie dodaj produkt do koszyka i sfinalizuj zakup przez agenta Kakobuy.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 6 - Comparison Table */}
            <section id="porownanie" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                6. Kakobuy Spreadsheet vs inne narzędzia
              </h2>
              <p className="text-foreground-700 text-base leading-relaxed mb-6 font-body">
                Jak wypada <strong className="text-foreground-950">Kakobuy Spreadsheet</strong> na tle innych dostępnych narzędzi do przeglądania produktów? Oto szczegółowe porównanie:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm font-body border-collapse">
                  <thead>
                    <tr className="bg-background-100/80">
                      <th className="text-left px-4 py-3 text-foreground-950 font-label font-semibold text-xs uppercase tracking-wider rounded-tl-lg">Funkcja</th>
                      <th className="text-left px-4 py-3 text-foreground-950 font-label font-semibold text-xs uppercase tracking-wider">Kakobuy Spreadsheet</th>
                      <th className="text-left px-4 py-3 text-foreground-950 font-label font-semibold text-xs uppercase tracking-wider">Wyszukiwarka platformy</th>
                      <th className="text-left px-4 py-3 text-foreground-950 font-label font-semibold text-xs uppercase tracking-wider rounded-tr-lg">Grupy społecznościowe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Organizacja kategorii', '⭐⭐⭐⭐⭐ Pełna', '⭐⭐⭐ Podstawowa', '⭐⭐ Słaba'],
                      ['Sprawdzeni sprzedawcy', '⭐⭐⭐⭐⭐ Tak', '⭐⭐⭐ Nie zawsze', '⭐⭐ Częściowo'],
                      ['Aktualność linków', '⭐⭐⭐⭐⭐ Regularna', '⭐⭐⭐⭐ Automatyczna', '⭐⭐⭐ Nieregularna'],
                      ['Łatwość użycia', '⭐⭐⭐⭐⭐ Bardzo łatwe', '⭐⭐⭐⭐ Łatwe', '⭐⭐⭐ Średnie'],
                      ['Dodatkowe informacje', '⭐⭐⭐⭐⭐ Bogate', '⭐⭐ Podstawowe', '⭐⭐⭐⭐ Dyskusje'],
                    ].map((row, idx) => (
                      <tr key={idx} className="border-b border-background-200/50 hover:bg-background-50/80 transition-colors">
                        <td className="px-4 py-3 text-foreground-800 font-medium whitespace-nowrap">{row[0]}</td>
                        <td className="px-4 py-3 text-foreground-600">{row[1]}</td>
                        <td className="px-4 py-3 text-foreground-600">{row[2]}</td>
                        <td className="px-4 py-3 text-foreground-600">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 7 */}
            <section id="wskazowki" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                7. Praktyczne wskazówki dla użytkowników
              </h2>

              <div className="bg-background-100/80 rounded-2xl p-6 md:p-8 mb-6">
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-4">Zalety korzystania z arkusza</h3>
                <ul className="space-y-3 font-body">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-foreground-700 text-sm md:text-base">Szybki dostęp do tysięcy sprawdzonych produktów w jednym miejscu</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-foreground-700 text-sm md:text-base">Możliwość porównywania produktów z różnych kategorii jednocześnie</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-foreground-700 text-sm md:text-base">Oszczędność pieniędzy dzięki dostępowi do najlepszych ofert</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-accent-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-foreground-700 text-sm md:text-base">Społeczność użytkowników dzieląca się opiniami i rekomendacjami</span>
                  </li>
                </ul>
              </div>

              <div className="bg-background-100/80 rounded-2xl p-6 md:p-8 mb-6">
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-4">Na co uważać</h3>
                <ul className="space-y-3 font-body">
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-secondary-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-foreground-700 text-sm md:text-base">Zawsze sprawdzaj aktualność arkusza przed dokonaniem zakupu</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-secondary-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-foreground-700 text-sm md:text-base">Korzystaj wyłącznie z zaufanych źródeł arkuszy Kakobuy</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-secondary-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-foreground-700 text-sm md:text-base">Nie udostępniaj swoich danych osobowych nieznanym sprzedawcom</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                8. FAQ &mdash; Najczęściej zadawane pytania
              </h2>

              <div className="space-y-4">
                {[
                  {
                    q: 'Czy Kakobuy Spreadsheet jest darmowy?',
                    a: 'Tak, dostęp do arkusza Kakobuy Spreadsheet jest całkowicie darmowy. Naszym celem jest ułatwienie dostępu do sprawdzonych produktów dla wszystkich użytkowników platformy Kakobuy.',
                  },
                  {
                    q: 'Jak często arkusz jest aktualizowany?',
                    a: 'Arkusz jest aktualizowany regularnie, zazwyczaj co kilka dni. Staramy się dodawać nowe produkty i usuwać nieaktualne linki, aby zapewnić najwyższą jakość informacji.',
                  },
                  {
                    q: 'Czy potrzebuję konta na Kakobuy aby korzystać z arkusza?',
                    a: 'Do przeglądania arkusza nie potrzebujesz konta. Jednak do dokonania zakupu na platformie Kakobuy konieczne jest posiadanie aktywnego konta użytkownika.',
                  },
                  {
                    q: 'Skąd pochodzą linki w arkuszu?',
                    a: 'Linki pochodzą od doświadczonych użytkowników platformy Kakobuy, którzy testują produkty i weryfikują sprzedawców. Każdy link jest sprawdzany przed dodaniem do arkusza.',
                  },
                  {
                    q: 'Co zrobić jeśli link nie działa?',
                    a: 'Jeśli znajdziesz nieaktywny link, skontaktuj się z nami. Staramy się regularnie weryfikować wszystkie linki, ale czasami produkty mogą zostać usunięte ze strony sprzedawcy.',
                  },
                ].map((faq, idx) => (
                  <details key={idx} className="group bg-background-100/80 rounded-xl border border-background-200/50">
                    <summary className="px-5 py-4 cursor-pointer font-label text-foreground-950 font-medium text-sm md:text-base flex items-center justify-between">
                      {faq.q}
                      <i className="ri-add-line group-open:hidden text-foreground-400"></i>
                      <i className="ri-subtract-line hidden group-open:block text-foreground-400"></i>
                    </summary>
                    <div className="px-5 pb-4 text-foreground-600 text-sm md:text-base leading-relaxed font-body">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <div className="mt-12 text-center bg-primary-50/50 rounded-2xl p-8 md:p-10 border border-primary-200/40">
              <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
                Gotowy aby zacząć korzystać z Kakobuy Spreadsheet?
              </h3>
              <p className="text-foreground-600 text-sm md:text-base mb-6 font-body max-w-xl mx-auto">
                Przejdź do głównego arkusza i odkryj tysiące sprawdzonych produktów w jednym miejscu.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors duration-200 font-label font-semibold text-sm whitespace-nowrap cursor-pointer"
                >
                  Przejdź do arkusza <i className="ri-arrow-right-line ml-1.5"></i>
                </a>
                <Link
                  to="/"
                  className="px-8 py-3.5 border-2 border-foreground-900 text-foreground-900 rounded-full hover:bg-foreground-900 hover:text-background-50 transition-colors duration-200 font-label font-semibold text-sm whitespace-nowrap cursor-pointer"
                >
                  ← Powrót do strony głównej
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdArticle) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFAQ) }} />
      </article>
      <Footer />
    </>
  );
}